/**
 * Extension URL for optimistic concurrency on ClinicalImpression.summary (FLDM-1314 / RFC 1881).
 */
export const REVIEW_BASE_FINGERPRINT_URL =
  'https://lifeomic.com/fhir/review-base-fingerprint';

function normalizeForFingerprint(text: string): string {
  return text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\n+$/, '');
}

/**
 * SHA-256 hex of UTF-8 body after line-ending normalisation and trailing-newline stripping.
 * Uses the Web Crypto API (`crypto.subtle`) so it runs in browsers and Node 18+ without Node's `crypto` module.
 *
 * Must match patient-service `computeContentFingerprint` and agents
 * `compute_content_fingerprint`. Golden vectors: this file's tests,
 * `patient-service/test/unit/utils/contentFingerprint.test.ts`,
 * `agents/test/lib/test_diff.py`.
 */
export const computeContentFingerprint = async (
  text: string,
): Promise<string> => {
  const normalized = normalizeForFingerprint(text);
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new Error(
      'Web Crypto API (globalThis.crypto.subtle) is not available',
    );
  }
  const data = new TextEncoder().encode(normalized);
  const hashBuffer = await subtle.digest('SHA-256', data);
  const bytes = new Uint8Array(hashBuffer);
  let hex = '';
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, '0');
  }
  return hex;
};

/**
 * Adds or replaces the review-base-fingerprint extension using the **base** summary
 * the user edited from (server state at read time), not the new summary being saved.
 */
export const upsertReviewBaseFingerprintExtension = async <
  T extends { url: string },
>(
  extensions: T[] | undefined,
  baseSummary: string,
): Promise<T[]> => {
  const arr: T[] = extensions ? extensions.map(e => ({ ...e })) : [];
  const fp = await computeContentFingerprint(baseSummary);
  const idx = arr.findIndex(e => e.url === REVIEW_BASE_FINGERPRINT_URL);
  const entry = {
    url: REVIEW_BASE_FINGERPRINT_URL,
    valueString: fp,
  } as unknown as T;
  if (idx >= 0) {
    arr[idx] = entry;
  } else {
    arr.push(entry);
  }
  return arr;
};
